import WasmController from "react-lib/frameworks/WasmController";

// Interface
import { State as MainState } from "../../../../../HIS/MainHISInterface";

// Common
import { combinePdfFiles, SetProperty } from "../../common/CommonInterface";

// Utils
import { beStringToAdString, getDefaultFilterDate } from "react-lib/utils/dateUtils";

// Serializer
import LeaveTypeSerializer from "issara-sdk/types/LeaveTypeSerializer_apps_HRM";
import LeaveRequestSerializer from "issara-sdk/types/LeaveRequestSerializer_apps_HRM";
import LeaveRequestActionSerializer from "issara-sdk/types/LeaveRequestActionSerializer_apps_HRM";
import LeaveRequestDocumentSerializer from "issara-sdk/types/LeaveRequestDocumentSerializer_apps_HRM";
import { LeaveRequestListType } from "../LeaveRequestListRendering";

// API
import LeaveTypeList from "issara-sdk/apis/LeaveTypeList_apps_HRM";
import LeaveRequestList from "issara-sdk/apis/LeaveRequestList_apps_HRM";
import LeaveRequestAction from "issara-sdk/apis/LeaveRequestActionList_apps_HRM";
import LeaveRequestDocument from "issara-sdk/apis/LeaveRequestDocumentList_apps_HRMM";
import LeaveRemaining from "issara-sdk/apis/LeaveRemainingList_apps_HRM";
import LeaveRequestDetail from "issara-sdk/apis/LeaveRequestDetail_apps_HRM";
import PaySlipList from "issara-sdk/apis/PaySlipList_apps_HRM";

import { ADMIN_TAB_LIST } from "react-lib/apps/USER/CardSettingDivision";
import PaySlipSerializer from "issara-sdk/types/PaySlipSerializer_apps_HRM";
import EmployeePayrollPaySlip from "issara-sdk/apis/EmployeePayrollPaySlip_apps_HRM";
import FormPaySlip from "../pdfForm/FormPaySlip";
import getPdfMake from "react-lib/appcon/common/pdfMake";

export type State = Partial<{
  // seq
  SelfServiceScheduleSequence: Partial<{
    sequenceIndex: "Start" | "Action" | null;
    // common
    leaveTypeList: LeaveTypeSerializer[];
    leaveRemaining: LeaveRemainingType;
    // tab
    workSchedule: WorkScheduleType;
    timeAttendance: any; // * (Tab 2) ยังไม่สร้าง Type
    leaveHistory: LeaveHistoryType; // * (Tab 3) ยังไม่ได้สร้าง Type
    paySlip: PaySlipType;
  }> | null;
}>;

export type PaySlipType = Partial<{
  paySlipList: Partial<PaySlipSerializer>[];
}>;

export type WorkScheduleType = Partial<{
  workScheduleList: any[];
  leaveRequest: Partial<LeaveRequestSerializer>;
  // leaveRemaining: LeaveRemainingType;
}>;

export type LeaveRemainingType = Partial<{
  approve_remaining_leave_days: number;
  max_leave_days: number;
  total_remaining_leave_days: number;
}>;

type FileType = {
  file: File;
};

export type TimeAttendanceType = Partial<{
  filter: TimeAttendanceFilterType;
  timeAttendanceList: any[];
}>;

type TimeAttendanceFilterType = Partial<{
  logType: any; // All, Clock-in, Clock-out
  startDate: string;
  endDate: string;
}>;

export type LeaveHistoryType = Partial<{
  filter: LeaveHistoryFilterType;
  leaveRequestDetail: LeaveRequestSerializer;
  leaveRequestList: LeaveRequestListType[];
  pagination: {
    activePage: number;
    items: LeaveRequestListType[];
    total: number;
  };
}>;

export type LeaveHistoryFilterType = Partial<{
  employee: number;
  employee_list: number[];
  leave_type: number;
  start_date: string;
  end_date: string;
}>;

export type PaySlipFilterType = Partial<{
  start_date: string;
  end_date: string;
}>;

type LeaveRequestListActionParams = {
  method: "list" | "create";
  controller: WasmController<State & PickedState, Event, Data, Props>;
  data?: LeaveRequestSerializer;
  params?: any;
};

type LeaveRequestDetailActionParams = {
  method: "retrieve" | "update" | "delete";
  controller: WasmController<State & PickedState, Event, Data, Props>;
  pk: number;
  data?: LeaveRequestSerializer;
};

export type Props = {};

type PickedState = Partial<
  Pick<
    MainState,
    | "adminMenuDivisions"
    | "buttonLoadCheck"
    | "errorMessage"
    | "masterOptions"
    | "searchedItemListWithKey"
    | "selectedDivision"
    | "successMessage"
  >
>;

export type PickedProps = Partial<Omit<PickedState, "">>;

// Sequence
type SeqState = {
  sequence: "SelfServiceSchedule";
  restart?: boolean;
  clear?: boolean;
};

// Action
type MenuActionMap = {
  Common: { action: "REMAIN"; leaveType: number };
  WorkSchedule:
    | { action: "FETCH" }
    | { action: "REQUEST"; saveLeaveRequest: Partial<LeaveRequestSerializer>; files?: FileType[] }
    | { action: "REMAIN"; leaveType: any }; // todo
  TimeAttendance: { action: "FETCH" }; // todo --
  LeaveHistory:
    | {
        action: "FETCH";
        filter?: LeaveHistoryFilterType;
        activePage?: number
      }
    | { action: "DETAIL"; requestId: number }
    | {
        action: "EDIT";
        requestId: number;
        editLeaveRequest: Partial<LeaveRequestSerializer>;
        files?: (FileType | { id: number })[];
        filter?: LeaveHistoryFilterType;
        activePage?: number;
      }
    | {
        action: "CANCEL";
        requestId: number;
        filter?: LeaveHistoryFilterType;
        activePage?: number;
      };
  TrainingHistory: { action: "FETCH" }; // todo --
  PaySlip: 
    | { action: "FETCH"; filter?: PaySlipFilterType }
    | { action: "DETAIL";
        employeePayroll: any;
        language: any;
      };
};

type MenuType = {
  [K in keyof MenuActionMap]: { menu: K } & MenuActionMap[K];
}[keyof MenuActionMap];

// export type RunSequence = <K extends keyof SeqAct>(params: SeqType<Pick<SeqAct, K>>) => void;
export type RunSequence = <M extends keyof MenuActionMap | undefined = undefined>(
  params: M extends keyof MenuActionMap ? SeqState & { menu: M } & MenuActionMap[M] : SeqState
) => void;

export type SetProp = SetProperty<State & PickedState>;

type Params<A extends keyof MenuActionMap> = { menu: A } & MenuActionMap[A];

type ExtractAction<
  M extends keyof MenuActionMap,
  A extends MenuActionMap[M] extends { action: string } ? MenuActionMap[M]["action"] : never
> = /*SeqState &*/ { menu: M } & Extract<MenuActionMap[M], { action: A }>;

export const StateInitial: State & PickedState = {
  // seq
  SelfServiceScheduleSequence: {
    sequenceIndex: null,
  },
  // SnackMessage
  successMessage: null,
  errorMessage: null,
};

export type Event =
  | { message: "RunSequence"; params: {} }
  | { message: "GetMasterData"; params: {} };

export type Data = {
  division?: number;
  userProfile?: Record<string, any>;
};

export const DataInitial = {};

export const CARD_NAME = "CardSelfServiceSchedule";

type Optional<T> = {
  [K in keyof T]: `${typeof CARD_NAME}_${T[K] & string}`;
};

export const MENUS = {
  Main: ["REMAIN"],
  WorkSchedule: ["FETCH", "REQUEST"],
  TimeAttendance: ["FETCH"],
  LeaveHistory: ["FETCH", "DETAIL", "EDIT", "CANCEL"],
  TrainingHistory: ["FETCH"],
  PaySlip: ["FETCH", "DETAIL"],
} as const;

// export const BTN_ACTS = Object.fromEntries(
//   Object.keys(MENUS).map((key) => [key, `${CARD_NAME}_${key}`])
// ) as Optional<typeof MENUS>;

// export const BTN_ACTS = Object.fromEntries(
//   Object.entries(MENUS).map(([menu]) => {
//     return [menu, {}];
//   })
// ) as {
//   [M in keyof MenuActionMap]: {
//     [A in MenuActionMap[M] extends { action: infer Act }
//       ? Act extends string ? Act : never
//       : never]: `${typeof CARD_NAME}_${M}_${A}`
//   }
// };

export const BTN_ACTS = Object.fromEntries(
  Object.entries(MENUS).map(([menu, actions]) => [
    menu,
    Object.fromEntries(actions.map((action) => [action, `${CARD_NAME}_${menu}_${action}`])),
  ])
) as {
  [M in keyof typeof MENUS]: {
    [A in (typeof MENUS)[M][number]]: `${typeof CARD_NAME}_${M}_${A}`;
  };
};

type Handler<P = any, R = any> = (
  controller: WasmController<State & PickedState, Event, Data, Props>,
  params: P
) => R;

export type TabMenu =
  | "LeaveHistory"
  | "TimeAttendance"
  | "TrainingHistory"
  | "WorkSchedule"
  | "PaySlip";

const Masters = [
  ["leaveType", {}],
  ["division", {}],
] as const;

export const leaveOptions = [
  { key: "F", value: "F", text: "Full Day" },
  { key: "H", value: "H", text: "Half Day" },
];

export const TAB_MENU_MAPPING = {
  // My shift schedule
  [ADMIN_TAB_LIST["My shift schedule"]]: "WorkSchedule",
  // Time Attendance
  [ADMIN_TAB_LIST["Time Attendance"]]: "TimeAttendance",
  // Leave History
  [ADMIN_TAB_LIST["Leave History"]]: "LeaveHistory",
  // Training History
  [ADMIN_TAB_LIST["Training History"]]: "TrainingHistory",
  [ADMIN_TAB_LIST["PaySlip"]]: "PaySlip",
} as const;

export const DEFAULT_LIMIT = 20;

/**========================================================================
 **                              START
 *========================================================================**/

export const Start: Handler<SeqState> = async (controller, params) => {
  // await HandlePaySlip(controller, {
  //   menu: "PaySlip",
  //   action: "FETCH",
  //   filter: {},
  // });
  const state = controller.getState();

  controller.handleEvent({ message: "GetMasterData", params: { masters: Masters } });

  // const [leaveTypeResp, leaveTypeErr] = await GetLeaveTypeList(controller, params);

  controller.setState(
    {
      SelfServiceScheduleSequence: {
        ...state.SelfServiceScheduleSequence,
        sequenceIndex: "Action",
        paySlip: {
          ...state.SelfServiceScheduleSequence?.paySlip,
        },
        // leaveTypeList: leaveTypeResp?.items || [],
      },
    },
    () => Action(controller, { ...params, menu: "WorkSchedule", action: "FETCH" })
  );
};

/**========================================================================
 **                              ACTIONS
 *========================================================================**/
export const Action: Handler<MenuType> = async (controller, params) => {
  const actionHandlers = {
    Common: {
      REMAIN: HandleGetLeaveRemain,
    },
    WorkSchedule: {
      FETCH: HandleWorkSchedule,
      REQUEST: HandleSaveLeaveRequest,
    },
    TimeAttendance: {
      FETCH: HandleTimeAttendance,
    },
    LeaveHistory: {
      FETCH: HandleLeaveHistory,
      DETAIL: HandleGetDatailLeaveRequest,
      EDIT: HandleEditLeaveRequest,
      CANCEL: HandleCancelLeaveRequest,
    },
    PaySlip: {
      FETCH: HandlePaySlip,
      DETAIL: HandlePaySlipFile
    },
  } as any;

  const { menu, action } = params as any;

  const handler = actionHandlers?.[menu]?.[action];

  if (handler) {
    return handler(controller, params as any);
  }
};

/**========================================================================
 **                              Common
 *========================================================================**/

const HandleGetLeaveRemain: Handler<ExtractAction<"Common", "REMAIN">> = async (
  controller,
  params
) => {
  const state = controller.getState();

  const [resp, err] = await GetLeaveRemaining(controller, params);

  controller.setState({
    SelfServiceScheduleSequence: {
      ...state.SelfServiceScheduleSequence,
      leaveRemaining: resp || null,
    },
  });
};

/**========================================================================
 **                              Work Schedule
 *========================================================================**/

const HandleWorkSchedule: Handler<ExtractAction<"WorkSchedule", "FETCH">> = async (
  controller,
  params
) => {
  const state = controller.getState();

  // const [requestListResp, requestListErr] = await GetLeaveRequestList(controller, params);

  controller.setState({
    SelfServiceScheduleSequence: {
      ...state.SelfServiceScheduleSequence,
      workSchedule: {
        ...state.SelfServiceScheduleSequence?.workSchedule,
        // workScheduleList: requestListResp,
      },
    },
  });
};

const HandleSaveLeaveRequest: Handler<ExtractAction<"WorkSchedule", "REQUEST">> = async (
  controller,
  params
) => {
  const state = controller.getState();

  const { saveLeaveRequest, files } = params;

  const data: Partial<LeaveRequestSerializer> = {
    ...saveLeaveRequest,
    start_date: beStringToAdString(saveLeaveRequest?.start_date || ""),
    end_date: beStringToAdString(saveLeaveRequest?.end_date || ""),
  };

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.WorkSchedule.REQUEST]: "LOADING",
    },
  });

  if (!saveLeaveRequest.leave_type) {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.WorkSchedule.REQUEST]: "ERROR",
      },
      errorMessage: {
        ...state.errorMessage,
        [CARD_NAME]: "Please select the leave type",
      },
    });

    return;
  }

  const [requestResp, requestErr] = await CreateRequestList(controller, {
    data: saveLeaveRequest,
    files,
  });

  if (requestErr) {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.WorkSchedule.REQUEST]: "ERROR",
      },
      errorMessage: {
        ...state.errorMessage,
        [CARD_NAME]: requestErr,
      },
    });
  } else {
    controller.setState({
      SelfServiceScheduleSequence: {
        ...state.SelfServiceScheduleSequence,
        workSchedule: {
          ...state.SelfServiceScheduleSequence?.workSchedule,
          workScheduleList: requestResp, // *
        },
      },
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.WorkSchedule.REQUEST]: "SUCCESS",
      },
      successMessage: {
        ...state.successMessage,
        [CARD_NAME]: "Save Leave Request Success",
      },
    });
  }

  HandleWorkSchedule(controller, { ...params, action: "FETCH" });
};

/**========================================================================
 **                            Time Attendance
 *========================================================================**/

const HandleTimeAttendance: Handler<ExtractAction<"TimeAttendance", "FETCH">> = async (
  controller,
  params
) => {
  const state = controller.getState();
};

/**========================================================================
 **                            Leave History
 *========================================================================**/

const HandleLeaveHistory: Handler<ExtractAction<"LeaveHistory", "FETCH">> = async (
  controller,
  params
) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.LeaveHistory.FETCH]: "LOADING",
    },
  });

  const { filter } = params;
  const { start_date, end_date } = getDefaultFilterDate();

  const filterFormatted: LeaveHistoryFilterType = {
    ...filter,
    start_date: beStringToAdString(filter?.start_date || start_date),
    end_date: beStringToAdString(filter?.end_date || end_date),
    employee_list: [controller.data.userProfile?.employee],
  };

  const activePage = params.activePage || 1;
  const offset = (activePage - 1) * DEFAULT_LIMIT;

  const [requestResp, requestErr] = await GetLeaveRequestList(controller, {
    filter: filterFormatted,
    limit: DEFAULT_LIMIT,
    offset,
  });

  if (requestErr) {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.LeaveHistory.FETCH]: "ERROR",
      },
      errorMessage: {
        ...state.errorMessage,
        [CARD_NAME]: requestErr,
      },
    });

    return;
  }

  controller.setState({
    SelfServiceScheduleSequence: {
      ...state.SelfServiceScheduleSequence,
      leaveHistory: {
        ...state.SelfServiceScheduleSequence?.leaveHistory,
        leaveRequestList: requestResp?.items || [],
        pagination: {
          activePage,
          items: requestResp?.items || [],
          total: requestResp?.total || 0,
        },
      },
    },
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.LeaveHistory.FETCH]: "SUCCESS",
    },
  });
};

const HandleGetDatailLeaveRequest: Handler<ExtractAction<"LeaveHistory", "DETAIL">> = async (
  controller,
  params
) => {
  const state = controller.getState();

  const { requestId } = params;

  const [detailResp, detailErr] = await GetLeaveRequestDetail(controller, { requestId });

  if (detailErr) {
    controller.setState({
      errorMessage: {
        ...state.errorMessage,
        [CARD_NAME]: detailErr,
      },
    });

    return;
  }

  controller.setState({
    SelfServiceScheduleSequence: {
      ...state.SelfServiceScheduleSequence,
      leaveHistory: {
        ...state.SelfServiceScheduleSequence?.leaveHistory,
        leaveRequestDetail: detailResp || null,
      },
    },
  });
};

const HandleEditLeaveRequest: Handler<ExtractAction<"LeaveHistory", "EDIT">> = async (
  controller,
  params
) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.LeaveHistory.EDIT]: "LOADING",
    },
  });

  const { editLeaveRequest, files, requestId, filter } = params;
  const activePage = params.activePage || 1;
  const pagination = state.SelfServiceScheduleSequence?.leaveHistory?.pagination;

  const [editResp, editErr] = await EditLeaveRequest(controller, {
    editLeaveRequest,
    files,
    requestId,
  });

  if (editErr) {
    controller.setState({
      SelfServiceScheduleSequence: {
        ...state.SelfServiceScheduleSequence,
        leaveHistory: {
          ...state.SelfServiceScheduleSequence?.leaveHistory,
          pagination: {
            ...pagination,
            activePage,
            items: pagination?.items || [],
            total: pagination?.total || 0,
          },
        },
      },
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.LeaveHistory.EDIT]: "ERROR",
      },
      errorMessage: {
        ...state.errorMessage,
        [CARD_NAME]: editErr,
      },
    });

    return;
  }

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.LeaveHistory.EDIT]: "SUCCESS",
    },
    successMessage: {
      ...state.successMessage,
      [CARD_NAME]: "Edit Leave Rrequest Success",
    },
  });

  HandleLeaveHistory(controller, { ...params, action: "FETCH", filter, activePage });
};

const HandleCancelLeaveRequest: Handler<ExtractAction<"LeaveHistory", "CANCEL">> = async (
  controller,
  params
) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.LeaveHistory.CANCEL]: "LOADING",
    },
  });

  const { requestId, filter } = params;
  const activePage = params.activePage || 1;
  const pagination = state.SelfServiceScheduleSequence?.leaveHistory?.pagination;

  const [cancelResp, cancelErr] = await CancelLeaveRequest(controller, { requestId });

  if (cancelErr) {
    controller.setState({
      SelfServiceScheduleSequence: {
        ...state.SelfServiceScheduleSequence,
        leaveHistory: {
          ...state.SelfServiceScheduleSequence?.leaveHistory,
          pagination: {
            ...pagination,
            activePage,
            items: pagination?.items || [],
            total: pagination?.total || 0,
          },
        },
      },
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.LeaveHistory.CANCEL]: "ERROR",
      },
      errorMessage: {
        ...state.errorMessage,
        [CARD_NAME]: cancelErr,
      },
    });

    return;
  }

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.LeaveHistory.CANCEL]: "SUCCESS",
    },
    successMessage: {
      ...state.successMessage,
      [CARD_NAME]: "Cancel Leave Request Success",
    },
  });

  HandleLeaveHistory(controller, { ...params, action: "FETCH", filter, activePage });
};

/**========================================================================
 **                            Pay Slip
 *========================================================================**/

const HandlePaySlip: Handler<ExtractAction<"PaySlip", "FETCH">> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.PaySlip.FETCH]: "LOADING",
    },
  });

  const { filter } = params || {};
  const filterFormatted = {
    start_date: filter?.start_date,
    end_date: filter?.end_date,
  };

  const [response, error] = await GetPaySlipList(controller, filterFormatted);

  if (error) {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.PaySlip.FETCH]: "ERROR",
      },
      errorMessage: {
        ...state.errorMessage,
        PaySlip: error,
      },
    });
    return;
  }

  console.log("Pay Slip Data: ", response);

  controller.setState({
    SelfServiceScheduleSequence: {
      ...state.SelfServiceScheduleSequence,
      paySlip: {
        ...state.SelfServiceScheduleSequence?.paySlip,
        paySlipList: response?.items || [],
      },
    },
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.PaySlip.FETCH]: "SUCCESS",
    },
  });
};

const HandlePaySlipFile: Handler<ExtractAction<"PaySlip", "DETAIL">> = async (controller, params) => {
  const state = controller.getState();
  
  const employeePayroll = params.employeePayroll;

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [`${employeePayroll}_${params.language}`]: "LOADING",
    },
  });

  const [data] = await EmployeePayrollPaySlip.get({
    apiToken: controller.apiToken,
    pk: employeePayroll,
    params: { language: params.language }
  });

  const pdfMake = await getPdfMake(true);

  const docDefinition = await FormPaySlip({
    ...data,
    language: params.language,
    passwordProtection: true
  });

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [`${employeePayroll}_${params.language}`]: null,
    },
  });

  const generatedPdf = pdfMake.createPdf(docDefinition);
  generatedPdf.open();
  setTimeout(() => {
    generatedPdf.download(data['file_name']);
  }, 1000);
}

/**========================================================================
 **                                APIS
 *========================================================================**/

const GetLeaveTypeList: Handler = async (controller, params) => {
  const state = controller.getState();

  const { filter } = params;

  const [resp, err] = await LeaveTypeList.list({
    apiToken: controller.apiToken,
    params: { ...filter },
    extra: {
      division: controller.data?.division,
    },
  });

  if (err) return [null, err];

  return [resp, null];
};

const GetLeaveRemaining: Handler = async (controller, params) => {
  const state = controller.getState();

  const { leaveType } = params;

  const [resp, err] = await LeaveRemaining.get({
    apiToken: controller.apiToken,
    params: {
      leave_type: leaveType,
    },
    extra: {
      division: controller.data?.division,
    },
  });

  if (err) return [null, err];

  return [resp, null];
};

const GetLeaveRequestList: Handler = async (controller, params) => {
  const state = controller.getState();

  const { filter, offset, limit } = params;

  return await HandleLeaveRequestListApi(controller, {
    method: "list",
    apiParams: { ...filter, limit, offset },
  });
};

const CreateRequestList: Handler = async (controller, params) => {
  const state = controller.getState();

  const { data, files } = params;

  const [resp, err] = await HandleLeaveRequestListApi(controller, {
    method: "create",
    data,
  });

  if (err) return [null, err];

  if (files && files.length > 0) {
    const requestId = resp.id;
    const [fileResp, fileErr] = await SaveAttachFileLeaveRequest(controller, { requestId, files });

    if (fileErr) return [null, fileErr];
  }

  return [resp, err];
};

const GetLeaveRequestDetail: Handler = async (controller, params) => {
  const state = controller.getState();

  const { requestId } = params;

  return await HandleLeaveRequestDetailApi(controller, {
    method: "retrieve",
    pk: requestId,
  });
};

const EditLeaveRequest: Handler = async (controller, params) => {
  const state = controller.getState();

  const { editLeaveRequest, files, requestId } = params;

  const [resp, err] = await HandleLeaveRequestDetailApi(controller, {
    method: "update",
    pk: requestId,
    data: { ...editLeaveRequest },
  });

  if (err) return [null, err];

  if (files && files.length > 0) {
    const [fileResp, fileErr] = await SaveAttachFileLeaveRequest(controller, { requestId, files });

    if (fileErr) return [null, fileErr];
  }

  return [resp, null];
};

const CancelLeaveRequest: Handler = async (controller, params) => {
  const state = controller.getState();

  const { requestId } = params;

  return await HandleLeaveRequestDetailApi(controller, {
    method: "delete",
    pk: requestId,
  });
};

const SaveAttachFileLeaveRequest: Handler = async (controller, params) => {
  const state = controller.getState();

  const { files, requestId } = params;

  const [resp, err] = await LeaveRequestDocument.create({
    apiToken: controller.apiToken,
    data: {
      leave_request: requestId,
      items: files,
    },
    extra: {
      division: controller.data.division,
    },
  });

  if (err) return [null, err];

  return [resp, err];
};

const GetPaySlipList: Handler = async (controller, params) => {
  const state = controller.getState();

  const { start_date, end_date } = params || {};
  const apiParams: Record<string, string> = {};

  if (start_date) apiParams.start_date = start_date;
  if (end_date) apiParams.end_date = end_date;

  const [resp, err] = await PaySlipList.list({
    apiToken: controller.apiToken,
    params: apiParams,
    extra: {
      division: controller.data?.division,
    },
  });

  if (err) return [null, err];
  console.log("Pay Slip Response:", resp);
  return [resp, null];
};

/**========================================================================
 **                            APIS HANDLER
 *========================================================================**/

const HandleLeaveRequestListApi: Handler<{
  method: "list" | "create";
  data?: LeaveRequestSerializer;
  apiParams?: any;
}> = async (controller, params) => {
  const { method, data, apiParams } = params;

  const commonParams = {
    apiToken: controller.apiToken,
    extra: {
      division: controller.data.division,
    },
  };

  const actionMethod = LeaveRequestList[method];

  if (!actionMethod) return [null, new Error(`Invalid method: ${method}`)];

  let callParams: any = commonParams;

  if (method === "list") {
    callParams = { ...commonParams, params: apiParams };
  } else if (method === "create") {
    callParams = { ...callParams, data };
  }

  const [resp, err] = await actionMethod(callParams);

  if (err) return [null, err];

  return [resp, null];
};

const HandleLeaveRequestDetailApi: Handler<{
  method: "retrieve" | "update" | "delete";
  pk: number;
  data?: LeaveRequestSerializer;
}> = async (controller, params) => {
  const { method, pk, data } = params;

  const commonParams = {
    apiToken: controller.apiToken,
    pk,
    extra: {
      division: controller.data.division,
    },
  };

  const actionMethod = LeaveRequestDetail[method];

  if (!actionMethod) return [null, new Error(`Invalid method: ${method}`)];

  const callParams = method === "update" ? { ...commonParams, data } : commonParams;

  const [resp, err] = await actionMethod(callParams);

  if (err) return [null, err];

  return [resp, null];
};
